import { useEffect, useRef, useState } from "react";
import { X, Plus, ChevronDown, Check, Upload, Paperclip } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { uploadResume, errText } from "../lib/api";

const baseInput =
  "bg-zinc-900/70 border-zinc-800 text-zinc-100 rounded-xl h-12 px-4 placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-brand-pink focus-visible:ring-offset-0 transition-colors duration-200";

export const Field = ({ field, value, onChange, error }) => {
  const id = field.id;
  const set = (v) => onChange(id, v);

  return (
    <div className="py-6 border-b border-zinc-900" data-testid={`field-${id}`}>
      <Label htmlFor={id} className="block text-zinc-100 text-base font-semibold mb-1">
        {field.label}
        {field.required && <span className="text-brand-pink ml-1">*</span>}
      </Label>
      {field.mode && <p className="font-mono-tech text-[10px] text-zinc-500 mt-1">{field.mode}</p>}
      {field.help && <p className="text-sm text-zinc-500 mt-2">{field.help}</p>}
      <div className="mt-4">
        {field.type === "longtext" && (
          <Textarea id={id} data-testid={`input-${id}`} rows={4} value={value || ""} onChange={(e) => set(e.target.value)}
            className={baseInput + " h-auto py-3 leading-relaxed"} placeholder="Type your answer…" />
        )}
        {(field.type === "text" || field.type === "email" || field.type === "url" || field.type === "number") && (
          <Input id={id} data-testid={`input-${id}`}
            type={field.type === "number" ? "number" : field.type === "email" ? "email" : "text"}
            value={value ?? ""} onChange={(e) => set(e.target.value)} className={baseInput}
            placeholder={field.type === "url" ? "https://…" : field.type === "number" ? "0" : "Type your answer…"} />
        )}
        {field.type === "range" && (
          <RangeField id={id} field={field} value={value} onChange={set} />
        )}
        {field.type === "select" && (
          <SingleDropdown id={id} options={field.options} value={value} onChange={set}
            searchable={field.searchable} />
        )}
        {field.type === "multiselect" && (
          <MultiDropdown id={id} groups={[[null, field.options]]} value={value || []} onChange={set} />
        )}
        {(field.type === "links" || field.type === "textlist") && (
          <RepeatableList id={id} value={value || []} onChange={set}
            placeholder={field.type === "links" ? "https://…" : "Name"}
            addLabel={field.type === "links" ? "Add another link" : "Add another name"} />
        )}
        {field.type === "grouped" && (
          <MultiDropdown id={id} groups={field.groups} value={value || []} onChange={set} />
        )}
        {field.type === "file" && (
          <FileField id={id} value={value} onChange={set} accept={field.accept} />
        )}
        {field.type === "pairs" && (
          <PairList id={id} value={value || []} onChange={set} options={field.options} addLabel={field.addLabel || "Add another"} />
        )}
      </div>
      {error && <p className="text-sm text-red-400 mt-2" data-testid={`error-${id}`}>{error}</p>}
    </div>
  );
};

const useOutside = (ref, onClose) => {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, onClose]);
};

const triggerClass =
  "flex w-full items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-900/70 px-4 py-3 text-left text-sm text-zinc-300 transition-colors duration-200 hover:border-brand-indigo/60";

const panelClass =
  "absolute z-30 mt-2 max-h-72 w-full overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-900 p-2 shadow-xl";

export const SingleDropdown = ({ id, options, value, onChange, searchable }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));
  const shown = searchable && query
    ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : options;
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={`dropdown-${id}`}
        onClick={() => { setOpen((o) => !o); setQuery(""); }} className={triggerClass}>
        <span className={value ? "" : "text-zinc-500"}>{value || "Select an option…"}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-zinc-500" />
      </button>
      {open && (
        <div className={panelClass} data-testid={`dropdown-panel-${id}`}>
          {searchable && (
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search…" data-testid={`dropdown-search-${id}`}
              className="mb-2 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-brand-indigo" />
          )}
          {shown.length === 0 && (
            <p className="px-3 py-2 text-sm text-zinc-500" data-testid={`dropdown-empty-${id}`}>No matches.</p>
          )}
          {shown.map((opt) => (
            <button key={opt} type="button" data-testid={`option-${id}-${opt}`}
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`block w-full rounded-lg px-3 py-2.5 text-left text-sm transition-colors duration-150 ${
                value === opt ? "bg-brand-pink text-zinc-950" : "text-zinc-300 hover:bg-zinc-800"}`}>
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const MultiDropdown = ({ id, groups, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));
  const toggle = (opt) =>
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={`dropdown-${id}`} onClick={() => setOpen((o) => !o)} className={triggerClass}>
        <span className={value.length ? "" : "text-zinc-500"}>
          {value.length ? `${value.length} selected` : "Select all that apply…"}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-zinc-500" />
      </button>
      {open && (
        <div className={panelClass} data-testid={`dropdown-panel-${id}`}>
          {groups.map(([group, options]) => (
            <div key={group || "all"} data-testid={group ? `group-${group}` : undefined} className="mb-1">
              {group && <p className="font-mono-tech px-3 pt-2 pb-1 text-[10px] text-zinc-500">{group}</p>}
              {options.map((opt) => {
                const active = value.includes(opt);
                return (
                  <button key={opt} type="button" data-testid={`option-${id}-${opt}`} onClick={() => toggle(opt)}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition-colors duration-150 ${
                      active ? "text-zinc-50" : "text-zinc-300"} hover:bg-zinc-800`}>
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      active ? "border-brand-indigo bg-brand-indigo" : "border-zinc-600"}`}>
                      {active && <Check className="h-3 w-3 text-white" />}
                    </span>
                    {opt}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
      {value.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {value.map((v) => (
            <span key={v} data-testid={`selected-${id}-${v}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-xs text-zinc-300">
              {v}
              <button type="button" data-testid={`remove-${id}-${v}`} onClick={() => toggle(v)} className="text-zinc-500 hover:text-red-400">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const RepeatableList = ({ id, value, onChange, placeholder, addLabel }) => {  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft("");
  };
  return (
    <div>
      {value.length > 0 && (
        <div className="mb-3 space-y-2">
          {value.map((v) => (
            <div key={v} data-testid={`chip-${id}-${v}`} className="flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">
              <span className="truncate text-sm text-zinc-300">{v}</span>
              <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} className="text-zinc-500 hover:text-red-400 transition-colors duration-200">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input data-testid={`input-${id}`} value={draft} onChange={(e) => setDraft(e.target.value)}
          onBlur={add}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className={baseInput + " flex-1"} placeholder={placeholder} />
        <Button type="button" data-testid={`add-${id}`} onClick={add}
          className="h-12 shrink-0 rounded-xl border border-zinc-700 bg-zinc-800 px-5 text-sm text-zinc-100 hover:bg-brand-pink hover:text-zinc-950 transition-colors duration-200">
          <Plus className="mr-1.5 h-4 w-4" /> {addLabel}
        </Button>
      </div>
    </div>
  );
};

export const PairList = ({ id, value, onChange, options, addLabel }) => {
  const update = (i, patch) => onChange(value.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  return (
    <div className="space-y-3">
      {value.map((row, i) => (
        <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4" data-testid={`pair-${id}-${i}`}>
          <div className="flex flex-wrap gap-2">
            {options.map((opt) => (
              <button key={opt} type="button" data-testid={`pair-${id}-${i}-${opt}`} onClick={() => update(i, { platform: opt })}
                className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors duration-200 ${
                  row.platform === opt ? "bg-brand-pink border-brand-pink text-zinc-950" : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-brand-pink/60"}`}>
                {opt}
              </button>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Input data-testid={`pair-url-${id}-${i}`} value={row.url || ""} onChange={(e) => update(i, { url: e.target.value })}
              className={baseInput + " flex-1"} placeholder="Profile link or @handle" />
            <Button type="button" data-testid={`pair-remove-${id}-${i}`} onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              className="h-12 shrink-0 rounded-xl border border-zinc-800 bg-transparent px-4 text-zinc-500 hover:border-red-500/60 hover:text-red-400">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" data-testid={`add-${id}`} onClick={() => onChange([...value, { platform: options[0], url: "" }])}
        className="h-12 rounded-xl border border-zinc-700 bg-zinc-800 px-5 text-sm text-zinc-100 hover:bg-brand-pink hover:text-zinc-950 transition-colors duration-200">
        <Plus className="mr-1.5 h-4 w-4" /> {addLabel}
      </Button>
    </div>
  );
};

export const FileField = ({ id, value, onChange, accept }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [failure, setFailure] = useState(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFailure(null);
    setProgress(0);
    try {
      const data = await uploadResume(file, (pct) => setProgress(pct));
      onChange({ file_id: data.file_id, filename: data.filename, size: data.size });
    } catch (err) {
      setFailure(errText(err));
    } finally {
      setProgress(null);
    }
  };

  if (value?.file_id) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3"
        data-testid={`file-attached-${id}`}>
        <span className="flex min-w-0 items-center gap-2.5 text-sm text-zinc-300">
          <Paperclip className="h-4 w-4 shrink-0 text-brand-indigo" />
          <span className="truncate">{value.filename}</span>
        </span>
        <button type="button" data-testid={`file-remove-${id}`} onClick={() => onChange(null)}
          className="shrink-0 text-zinc-500 transition-colors duration-200 hover:text-red-400">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <input ref={inputRef} type="file" accept={accept} onChange={pick} className="hidden"
        data-testid={`file-input-${id}`} />
      <Button type="button" data-testid={`file-pick-${id}`} disabled={progress !== null}
        onClick={() => inputRef.current?.click()}
        className="h-12 rounded-xl border border-zinc-700 bg-zinc-800 px-5 text-sm text-zinc-100 transition-colors duration-200 hover:bg-brand-pink hover:text-zinc-950">
        <Upload className="mr-2 h-4 w-4" />
        {progress !== null ? `Uploading… ${progress}%` : "Choose file"}
      </Button>
      {progress !== null && (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-zinc-800" data-testid={`file-progress-${id}`}>
          <div className="brand-gradient-rule h-full rounded-full transition-all duration-200"
            style={{ width: `${Math.max(progress, 4)}%` }} />
        </div>
      )}
      {failure && <p className="mt-3 text-sm text-red-400" data-testid={`file-error-${id}`}>{failure}</p>}
    </div>
  );
};

export const inputClass = baseInput;

const money = (n) => (n >= 30000 ? "$30,000+" : `$${n.toLocaleString()}`);

export const RangeField = ({ id, field, value, onChange }) => {
  const { min, max, step } = field;
  const low = value?.low ?? 1000;
  const high = value?.high ?? 5000;
  const set = (patch) => {
    const next = { low, high, ...patch };
    if (next.low > next.high) {
      if (patch.low !== undefined) next.high = next.low;
      else next.low = next.high;
    }
    onChange(next);
  };
  const band = (field.bands || []).find(([l, h]) => high > l && high <= h) || (field.bands || [])[0];
  const pct = (n) => ((n - min) / (max - min)) * 100;

  return (
    <div data-testid={`range-${id}`}>
      <p className="font-display text-3xl font-black text-zinc-50 md:text-4xl" data-testid={`range-value-${id}`}>
        {money(low)} <span className="text-zinc-600">–</span> {money(high)}
      </p>

      <div className="relative mt-8 h-8">
        <div className="absolute left-0 right-0 top-3 h-1.5 rounded-full bg-zinc-800" />
        <div className="brand-gradient-rule absolute top-3 h-1.5 rounded-full"
          style={{ left: `${pct(low)}%`, width: `${Math.max(pct(high) - pct(low), 1)}%` }} />
        <input type="range" aria-label="Minimum budget" data-testid={`range-low-${id}`}
          min={min} max={max} step={step} value={low}
          onChange={(e) => set({ low: Number(e.target.value) })}
          className="range-thumb absolute inset-x-0 top-0 h-8 w-full appearance-none bg-transparent" />
        <input type="range" aria-label="Maximum budget" data-testid={`range-high-${id}`}
          min={min} max={max} step={step} value={high}
          onChange={(e) => set({ high: Number(e.target.value) })}
          className="range-thumb absolute inset-x-0 top-0 h-8 w-full appearance-none bg-transparent" />
      </div>

      <div className="font-mono-tech mt-2 flex justify-between text-[10px] text-zinc-600">
        <span>{money(min)}</span>
        <span>{money(max)}</span>
      </div>

      {band && (
        <p className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 text-sm leading-relaxed text-zinc-300" data-testid={`range-band-${id}`}>
          {band[2]}
        </p>
      )}
    </div>
  );
};
